import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type JSX,
} from 'react';
import type { Asset, Booking, Contact } from '@taki/data-model';
import {
  Button,
  IconCalendarMark,
  Input,
  Modal,
  ScreenLayout,
  ViewHost,
  usePendingAppAction,
  usePref,
  useScreen,
} from '@taki/ui';
import type { Column, ScreenFilterDef, ViewBinding } from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  RESERVATIONS_APP_ID,
  RESERVATIONS_SCREEN_ID,
  RESERVATIONS_VIEW_TYPES,
} from './views.js';
import {
  BOOKING_STATUS_OPTIONS,
  BOOKING_TYPE,
  CATEGORY_OPTIONS,
  INITIAL_STATUS_VALUES,
  MATERIEL_AVAILABLE,
  MATERIEL_SWITCHABLE,
  MATERIEL_TYPE,
  SOURCE_OPTIONS,
  addDays,
  assetIdsOf,
  assetLabelOf,
  categoriesOf,
  categoryLabelsOf,
  categoryOf,
  clientInitialsOf,
  clientNameOf,
  crewContactOf,
  dailyRateOf,
  dailyTotalOf,
  depositOf,
  formatDateTime,
  formatEuros,
  fromDayAndTime,
  isOverdue,
  labelOf,
  materialCountLabel,
  materielLabelsOf,
  matchSection,
  parcStatusFor,
  pickupNoteOf,
  projectNameOf,
  rentalDays,
  setDirectory,
  statusOf,
  subtitleOf,
  toDayInput,
  toTimeInput,
  totalAmountOf,
  type BookingStatus,
  type SectionId,
} from './data.js';
import {
  DEFAULT_DAYS,
  DEFAULT_DAYS_MAX,
  DEFAULT_DAYS_MIN,
  DEFAULT_DAYS_PREF_KEY,
  DEFAULT_FILTER_ID,
  DEFAULT_FILTERS,
  DEFAULT_STATS,
  FILTER_DEFS,
  FILTERS_PREF_KEY,
  STAT_DEFS,
  STATS_MAX,
  STATS_PREF_KEY,
  type ReservationFilterId,
  type ReservationStatId,
} from './settings-defs.js';

/**
 * Ecran Locations de l'app `reservations` : qui a reserve quoi, du quand au
 * quand, a quel statut, pour quel montant, et surtout ce qui PART aujourd'hui,
 * ce qui RENTRE aujourd'hui et ce qui est EN RETARD.
 *
 * Structure imposee par la cage IA (docs/GABARIT-ECRAN.md) : UN `ScreenLayout`
 * a la racine, le `Modal` en frere, acces data 100% via `@taki/sdk`
 * (`useTakiApp`), zone 5 pilotee par les VUES du socle (`useScreen` +
 * `ViewHost`), etats standard delegues au `ViewHost`.
 *
 * TROIS choses qui meritent d'etre lues avant de modifier ce fichier :
 *
 * 1. LES BINDINGS SONT DES CONSTANTES DE MODULE. `useScreen` lit les bindings
 *    par REFERENCE (le memo de projection ne depend pas d'eux) : un binding
 *    recree a chaque rendu ne serait pris en compte qu'au prochain changement de
 *    donnees. Tout ce dont ils ont besoin en plus (le nom du client, les
 *    categories du materiel) vit dans l'annuaire de `data.ts`, rempli par le
 *    chargeur AVANT que les locations n'arrivent.
 *
 * 2. LE MENU DE LA ZONE 1 SEGMENTE UNE SEULE COLLECTION, ET LA SECTION
 *    APPARTIENT AU SOCLE. Les trois sections (Planning, Aujourd'hui, En retard)
 *    decoupent les MEMES locations : la section vit dans la requete d'ecran
 *    (`screen.section` / `screen.setSection`, portee par `query.section`), donc
 *    dans l'adresse (`?s=retard`), donc dans un lien partageable et dans
 *    l'historique du navigateur. Le chargeur la lit dans la requete qu'il
 *    RECOIT, et le core le rappelle tout seul quand elle change : l'ecran n'a
 *    donc plus ni `useState` de section, ni effet de rechargement maison (il
 *    ferait aujourd'hui une seconde lecture pour rien). Les COMPTEURS du menu,
 *    eux, restent calcules sur le jeu COMPLET, jamais sur la section courante :
 *    un menu dont les compteurs disparaissent quand on clique dedans est un bug.
 *
 * 3. LE KANBAN ECRIT VRAIMENT. Le role `groupKey` porte `options()` ET `set()` :
 *    faire glisser une carte de "Confirmee" vers "Sortie" appelle
 *    `sdk.bookings.update`. Le `set()` d'un binding de module ne peut pas voir
 *    le SDK du composant : l'ecran depose donc sa fonction d'ecriture dans le
 *    relais ci-dessous, le temps de sa vie.
 *
 * 4. LE PARC SUIT LA LOCATION. Un statut de location qui change entraine le
 *    statut des materiels engages (`sorti` -> `loue`, `retourne` ou `annule` ->
 *    `disponible`) via `sdk.assets.update`, avec UN garde-fou : on ne bascule
 *    qu'entre `disponible` et `loue`, jamais un materiel que le parc declare
 *    `maintenance` ou `reforme`. La regle a UNE seule implementation ici
 *    (`syncParc`), appelee par le kanban ET par le formulaire : deux copies
 *    finiraient par diverger.
 */

/* ------------------------------------------------------------------ */
/* Le relais d'ecriture du kanban                                      */
/* ------------------------------------------------------------------ */

/**
 * Le SDK n'est disponible que dans le composant ; les bindings, eux, sont des
 * constantes de module. L'ecran depose ici sa fonction d'ecriture au montage et
 * la retire au demontage. Absente, le `set()` LEVE : `useScreen` remet alors la
 * carte dans sa colonne d'origine, au lieu de mentir a l'utilisateur.
 */
const statusRelay: { write: ((booking: Booking, status: string) => Promise<void>) | null } = {
  write: null,
};

/* ------------------------------------------------------------------ */
/* Ce que SONT les donnees : colonnes et bindings                      */
/* ------------------------------------------------------------------ */

/** "1 jour" / "5 jours" : le pluriel se dit, il ne se devine pas. */
function daysLabel(count: number): string {
  return count <= 1 ? `${count} jour` : `${count} jours`;
}

/**
 * Les colonnes du TABLEAU : comparer des locations entre elles. `value` sert au
 * tri, a l'export et a l'agregat (jamais `undefined` : une valeur absente vaut
 * `null` et finit en bas du tri) ; `render` n'est que de l'affichage.
 * La colonne Statut n'est pas ici : elle vient du role `status`, nomme par
 * l'option `statusLabel` du binding.
 */
const TABLE_COLUMNS: Column<Booking>[] = [
  { key: 'client', label: 'Client', value: (booking) => clientNameOf(booking), sortable: true },
  { key: 'start', label: 'Depart', value: (booking) => booking.startAt ?? null, sortable: true },
  { key: 'end', label: 'Retour', value: (booking) => booking.endAt ?? null, sortable: true },
  {
    key: 'items',
    label: 'Materiels',
    value: (booking) => assetIdsOf(booking).length,
    sortable: true,
  },
  {
    key: 'amount',
    label: 'Montant',
    value: (booking) => totalAmountOf(booking),
    render: (booking) => formatEuros(totalAmountOf(booking)),
    sortable: true,
    agg: 'sum',
  },
  {
    key: 'deposit',
    label: 'Acompte',
    value: (booking) => depositOf(booking),
    render: (booking) => formatEuros(depositOf(booking)),
    sortable: true,
  },
];

/**
 * Les colonnes de la FICHE : tout ce qu'un comptoir demande au telephone.
 * Le nom du projet n'y figure pas, c'est deja le TITRE de la fiche.
 *
 * ELLES PORTENT AUSSI LES DATES ET LES CATEGORIES, et c'est voulu : la fiche du
 * socle nomme les roles avec SON vocabulaire ("Debut", "Fin", "Couloir") et
 * ignore le `label` d'un `FieldMap`. Declarer ces roles ICI aurait donc affiche
 * les memes dates deux fois, sous deux noms differents. Le binding de la fiche
 * ne garde donc que les roles de l'EN-TETE ; tout le detail passe par ces
 * colonnes, avec les mots du comptoir.
 */
const RECORD_COLUMNS: Column<Booking>[] = [
  { key: 'client', label: 'Client', value: (booking) => clientNameOf(booking) },
  {
    key: 'crew',
    label: 'Contact tournage',
    value: (booking) => (crewContactOf(booking) === '' ? null : crewContactOf(booking)),
  },
  {
    key: 'start',
    label: 'Depart',
    value: (booking) => booking.startAt ?? null,
    render: (booking) => formatDateTime(booking.startAt),
  },
  {
    key: 'end',
    label: 'Retour',
    value: (booking) => booking.endAt ?? null,
    render: (booking) => formatDateTime(booking.endAt),
  },
  {
    key: 'days',
    label: 'Duree',
    value: (booking) => rentalDays(booking.startAt, booking.endAt),
    render: (booking) => daysLabel(rentalDays(booking.startAt, booking.endAt)),
  },
  {
    key: 'items',
    label: 'Materiel loue',
    value: (booking) => (materielLabelsOf(booking) === '' ? null : materielLabelsOf(booking)),
  },
  {
    key: 'categories',
    label: 'Categories engagees',
    value: (booking) => (categoryLabelsOf(booking) === '' ? null : categoryLabelsOf(booking)),
  },
  {
    key: 'deposit',
    label: 'Acompte',
    value: (booking) => depositOf(booking),
    render: (booking) => formatEuros(depositOf(booking)),
  },
  {
    key: 'amount',
    label: 'Montant total',
    value: (booking) => totalAmountOf(booking),
    render: (booking) => formatEuros(totalAmountOf(booking)),
  },
  {
    key: 'source',
    label: 'Canal',
    value: (booking) => {
      const label = labelOf(SOURCE_OPTIONS, booking.source);
      return label === '' ? null : label;
    },
  },
  {
    key: 'note',
    label: 'Note de depart',
    value: (booking) => (pickupNoteOf(booking) === '' ? null : pickupNoteOf(booking)),
  },
];

/**
 * UN BINDING PAR VUE : la meme location ne se raconte pas pareil en liste et en
 * calendrier. LES CINQ SONT NOURRIES, aucune entree grisee : c'est la promesse
 * de l'app vitrine du pack.
 */
const RESERVATION_BINDINGS: ViewBinding<Booking>[] = [
  {
    /* LISTE : reconnaitre une location d'un coup d'oeil. Le sous-titre porte le
       client, les dates, le nombre de materiels, et "En retard" EN TETE quand le
       retour est depasse : c'est l'information qui coute de l'argent. */
    type: 'core.liste',
    roles: {
      title: { get: (booking) => projectNameOf(booking), empty: 'Projet non renseigne' },
      subtitle: { get: (booking) => subtitleOf(booking, new Date()) },
      avatar: { get: (booking) => clientInitialsOf(booking) },
      status: { get: (booking) => statusOf(booking), options: () => BOOKING_STATUS_OPTIONS },
    },
  },
  {
    /* TABLEAU : comparer les montants, les durees, les volumes. */
    type: 'core.tableau',
    roles: {
      title: { get: (booking) => projectNameOf(booking), empty: 'Projet non renseigne' },
      avatar: { get: (booking) => clientInitialsOf(booking) },
      status: { get: (booking) => statusOf(booking), options: () => BOOKING_STATUS_OPTIONS },
    },
    columns: TABLE_COLUMNS,
    // Sac libre, lu par ce SEUL type de vue : ici le titre est un projet.
    options: { titleLabel: 'Projet', statusLabel: 'Statut' },
  },
  {
    /* KANBAN : faire avancer une location a la souris. `options()` est
       obligatoire (sinon colonnes fantomes) et `set()` est ce qui rend la carte
       glissable : glisser "Confirmee" vers "Sortie", c'est le materiel qui part.
       Glisser vers "Annulee" sort la location du planning au rechargement. */
    type: 'core.kanban',
    roles: {
      title: { get: (booking) => projectNameOf(booking), empty: 'Projet non renseigne' },
      subtitle: { get: (booking) => subtitleOf(booking, new Date()) },
      avatar: { get: (booking) => clientInitialsOf(booking) },
      groupKey: {
        get: (booking) => statusOf(booking),
        options: () => BOOKING_STATUS_OPTIONS,
        set: async (booking, value) => {
          if (statusRelay.write == null) {
            throw new Error("L'ecran n'est pas pret : recharge la page et reessaie.");
          }
          await statusRelay.write(booking, String(value));
        },
      },
    },
  },
  {
    /* CALENDRIER : placer dans le temps. `dateEnd` etale la location sur tous
       les jours couverts, et `lane` est MULTI-VALUE (`getMany`) : une location
       de camera ET de lumiere occupe DEUX couloirs, exactement le cas prevu par
       le contrat des vues ("1 reservation, 3 salles"). */
    type: 'core.calendrier',
    roles: {
      title: { get: (booking) => projectNameOf(booking), empty: 'Projet non renseigne' },
      subtitle: { get: (booking) => subtitleOf(booking, new Date()) },
      status: { get: (booking) => statusOf(booking), options: () => BOOKING_STATUS_OPTIONS },
      dateStart: { get: (booking) => booking.startAt ?? null },
      dateEnd: { get: (booking) => booking.endAt ?? null },
      lane: {
        get: (booking) => categoriesOf(booking)[0] ?? null,
        getMany: (booking) => categoriesOf(booking),
        options: () => CATEGORY_OPTIONS,
      },
    },
  },
  {
    /* FICHE : consulter et repondre au telephone. Les roles nourrissent
       l'EN-TETE (titre, sous-titre, pastille, statut) ; tout le detail passe par
       les colonnes, avec le vocabulaire du comptoir (voir RECORD_COLUMNS). */
    type: 'core.fiche',
    roles: {
      title: { get: (booking) => projectNameOf(booking), empty: 'Projet non renseigne' },
      subtitle: { get: (booking) => subtitleOf(booking, new Date()) },
      avatar: { get: (booking) => clientInitialsOf(booking) },
      status: { get: (booking) => statusOf(booking), options: () => BOOKING_STATUS_OPTIONS },
    },
    columns: RECORD_COLUMNS,
  },
];

/**
 * Ce que chaque pastille de la zone 4 garde. Le `match` est ce qui permet au
 * CORE de filtrer : il s'applique AVANT la projection, donc il vaut pour les
 * cinq vues d'un coup. Les pastilles RAFFINENT la section du menu, elles ne la
 * remplacent pas.
 */
const FILTER_MATCH: Record<ReservationFilterId, ((booking: Booking) => boolean) | undefined> = {
  toutes: undefined,
  option: (booking) => statusOf(booking) === 'option',
  confirmee: (booking) => statusOf(booking) === 'confirme',
  sortie: (booking) => statusOf(booking) === 'sorti',
};

/** Les trois sections du menu de la zone 1, dans l'ordre du comptoir. */
const SECTION_DEFS: ReadonlyArray<{ id: SectionId; label: string }> = [
  { id: 'planning', label: 'Planning' },
  { id: 'aujourdhui', label: "Aujourd'hui" },
  { id: 'retard', label: 'En retard' },
];

/** La section de depart, et le repli de tout ce qui n'est pas au catalogue. */
const DEFAULT_SECTION: SectionId = 'planning';

/**
 * La section demandee, ramenee au catalogue ci-dessus.
 *
 * Le socle ne CONNAIT pas la liste des sections (elle vit dans les `menuItems`
 * que l'ecran donne au gabarit) : depuis que la section voyage dans l'adresse,
 * `query.section` peut donc porter n'importe quelle chaine, tapee a la main ou
 * heritee d'un vieux lien. On la ramene sur le Planning quand elle est inconnue,
 * plutot que de la faire passer telle quelle pour une `SectionId` : `matchSection`
 * rend `true` par defaut, donc un `?s=nimporte-quoi` afficherait TOUTES les
 * locations, annulees comprises, avec un menu ou aucune entree n'est allumee.
 * Passer par la liste plutot que par un transtypage evite en prime le seul cast
 * du fichier : `find` rend deja une `SectionId`, elle est vraie par construction.
 */
function toSection(raw: string | null | undefined): SectionId {
  return SECTION_DEFS.find((def) => def.id === raw)?.id ?? DEFAULT_SECTION;
}

/** Valeur de la liste deroulante "client" quand on cree le client a la volee. */
const NEW_CLIENT = '__nouveau-client__';

/**
 * Classe commune des listes deroulantes du formulaire : exactement les mesures
 * du champ `Input` de @taki/ui (hauteur, rayon, surface, bordure), pour que les
 * deux s'alignent sur une meme ligne. Que des tokens, aucune couleur en dur,
 * aucune taille en px.
 */
const SELECT_CLASS =
  'h-[3.125rem] w-full rounded-[0.8125rem] border border-border bg-surface-1 px-4 text-sm ' +
  'text-ink-900 transition-colors hover:border-border-strong focus-visible:outline-none ' +
  'focus-visible:border-accent-border focus-visible:shadow-ring-soft';

/** Un montant saisi, en nombre. Une saisie illisible vaut 0, jamais NaN. */
function toAmount(raw: string): number {
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0;
}

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

export function ReservationsScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  /* ---- Prefs partagees avec settings.tsx (hub Parametres du shell) ---- */

  const [filterVisibility] = usePref<Record<string, boolean>>(FILTERS_PREF_KEY, DEFAULT_FILTERS);
  const [statIds] = usePref<string[]>(STATS_PREF_KEY, DEFAULT_STATS);
  const [defaultDaysPref] = usePref<number>(DEFAULT_DAYS_PREF_KEY, DEFAULT_DAYS);
  const defaultDays = Math.min(
    DEFAULT_DAYS_MAX,
    Math.max(DEFAULT_DAYS_MIN, Math.round(defaultDaysPref) || DEFAULT_DAYS),
  );

  /* ---- Les jeux COMPLETS (compteurs, stats, formulaire) ---- */

  // La SECTION n'est plus ici : elle vit dans la requete d'ecran, sous
  // `screen.section` (point 2 de l'en-tete). Ce qui reste local, ce sont les
  // jeux complets, qui ne dependent d'AUCUNE section.
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [clients, setClients] = useState<Contact[]>([]);
  const [parc, setParc] = useState<Asset[]>([]);

  // Pastilles reellement passees au core : les visibles selon la pref (cle
  // absente = visible, seul un `false` explicite masque).
  const filterDefs = useMemo<ScreenFilterDef<Booking>[]>(
    () =>
      FILTER_DEFS.filter((def) => filterVisibility[def.id] !== false).map((def) => {
        const match = FILTER_MATCH[def.id];
        return match == null
          ? { id: def.id, label: def.label }
          : { id: def.id, label: def.label, match };
      }),
    [filterVisibility],
  );

  /*
   * L'ETAT D'ECRAN, tenu par le core. Le chargeur fait TROIS lectures d'un coup :
   * les locations (la collection de l'ecran), les contacts (les clients) et les
   * assets (le parc de l'app Inventaire). Les deux dernieres remplissent
   * l'annuaire de `data.ts` AVANT que les locations ne soient rendues : quand la
   * projection tourne, les noms de clients et les categories de materiel sont la.
   */
  const screen = useScreen<Booking>({
    appId: RESERVATIONS_APP_ID,
    screenId: RESERVATIONS_SCREEN_ID,
    // La section arrive par la REQUETE : c'est le socle qui la tient, et c'est
    // lui qui rappelle ce chargeur quand elle change (elle fait partie de sa cle
    // de chargement, au meme titre que la page ou l'intervalle).
    load: async (query) => {
      const [bookings, contactList, assetList] = await Promise.all([
        sdk.bookings.list(),
        sdk.contacts.list(),
        sdk.assets.list(),
      ]);
      // Le parc cinema coexiste avec les assets des autres apps de demo : on ne
      // montre JAMAIS un chat dans le materiel de tournage.
      const materiel = assetList.filter((asset) => asset.type === MATERIEL_TYPE);
      const locations = bookings.filter((booking) => booking.type === BOOKING_TYPE);
      setDirectory(contactList, materiel);
      setAllBookings(locations);
      setClients(contactList);
      setParc(materiel);
      const now = new Date();
      return locations.filter((booking) => matchSection(booking, toSection(query.section), now));
    },
    getId: (booking) => booking.id,
    // Entree a froid (reprise de session sur une fiche) : on charge la location
    // seule plutot que toute la collection.
    get: (id) => sdk.bookings.get(id),
    types: RESERVATIONS_VIEW_TYPES,
    bindings: RESERVATION_BINDINGS,
    filters: filterDefs,
    defaultFilterId: DEFAULT_FILTER_ID,
    defaultView: 'core.liste',
    defaultSection: DEFAULT_SECTION,
  });

  const { markRead, openId, reload, setFilter, setSection } = screen;
  const activeFilterId = screen.query.filterId;

  // La section courante, telle que le socle la tient (et telle que l'adresse la
  // porte). AUCUN effet de rechargement en face : changer de section change la
  // cle de chargement du core, qui rappelle le chargeur tout seul. Le vieux
  // `useEffect(() => reload(), [section])` ferait aujourd'hui une seconde
  // lecture pour rien, en plus de celle du core.
  const section = toSection(screen.section);

  /* ------------------------------------------------------------------ */
  /* LE PARC SUIT LA LOCATION                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Le parc n'a pas suivi : le message a montrer. Une seule alerte pour les
   * deux chemins d'ecriture, rendue dans un `Modal` frere du `ScreenLayout`
   * (meme mecanique que l'erreur du formulaire : un etat, un paragraphe
   * `role="alert"` en ton danger).
   */
  const [parcError, setParcError] = useState<string | null>(null);

  /**
   * LA REGLE, EN UN SEUL ENDROIT : le kanban et le formulaire l'appellent tous
   * les deux. Une location qui passe a `sorti` fait passer ses materiels a
   * `loue` ; `retourne` ou `annule` les rend `disponible` ; `option` et
   * `confirme` ne touchent a rien (`parcStatusFor`, dans data.ts).
   *
   * Quatre engagements tenus ici :
   *
   * 1. APRES LA LOCATION, jamais avant. L'appelant a deja obtenu son
   *    `sdk.bookings.update` : c'est la location qui fait foi, le parc n'est
   *    que sa consequence.
   * 2. LE GARDE-FOU. On ne bascule QU'ENTRE `disponible` et `loue`. Un materiel
   *    que le parc declare `maintenance` ou `reforme` n'est jamais touche,
   *    dans aucun sens. Et le statut est RELU juste avant d'ecrire
   *    (`sdk.assets.get`) plutot que pris dans la copie locale : l'app
   *    Inventaire a pu envoyer la camera a l'atelier depuis notre dernier
   *    chargement, et c'est precisement le cas que le garde-fou doit voir.
   * 3. EN PARALLELE. Une location engage jusqu'a six materiels : on ne fait pas
   *    attendre six allers-retours mis bout a bout.
   * 4. NE LEVE JAMAIS. Un echec de parc ne doit ni faire reculer la carte du
   *    kanban (la location, elle, est bien enregistree, la faire reculer serait
   *    un mensonge) ni casser l'ecran. La fonction rend un message, l'appelant
   *    l'affiche.
   */
  const syncParc = useCallback(
    async (status: string, assetIds: string[]): Promise<string | null> => {
      const target = parcStatusFor(status);
      if (target == null || assetIds.length === 0) return null;

      const outcomes = await Promise.allSettled(
        assetIds.map(async (assetId) => {
          const asset = await sdk.assets.get(assetId);
          // Materiel disparu, archive, ou appartenant a une autre app : rien.
          if (asset == null || asset.archivedAt != null || asset.type !== MATERIEL_TYPE) return;
          const current = asset.status ?? '';
          // Le garde-fou, puis l'economie : rien a ecrire s'il y est deja.
          if (!MATERIEL_SWITCHABLE.includes(current) || current === target) return;
          await sdk.assets.update(asset.id, { status: target });
        }),
      );

      const failed = outcomes.filter((outcome) => outcome.status === 'rejected').length;
      if (failed === 0) return null;
      return `La location est bien enregistree, mais le parc n'a pas suivi pour ${materialCountLabel(failed)}. La disponibilite est a corriger dans l'app Inventaire.`;
    },
    [sdk],
  );

  // Le relais d'ecriture du kanban vit le temps de l'ecran. On recharge apres
  // le depot : les compteurs du menu et les stats disent alors la meme verite
  // que les colonnes.
  useEffect(() => {
    statusRelay.write = async (booking, status) => {
      await sdk.bookings.update(booking.id, { status });
      // Le parc suit APRES, et seulement si la ligne du dessus a reussi : une
      // ecriture de location qui leve remet la carte dans sa colonne, il n'y a
      // alors rien a repercuter.
      const problem = await syncParc(status, assetIdsOf(booking));
      reload();
      if (problem != null) setParcError(problem);
    };
    return () => {
      statusRelay.write = null;
    };
  }, [sdk, reload, syncParc]);

  // Si la pastille ACTIVE vient d'etre masquee dans les reglages, le filtre
  // retombe sur le defaut (jamais de filtre actif invisible).
  useEffect(() => {
    if (
      activeFilterId != null &&
      activeFilterId !== DEFAULT_FILTER_ID &&
      filterVisibility[activeFilterId] === false
    ) {
      setFilter(DEFAULT_FILTER_ID);
    }
  }, [filterVisibility, activeFilterId, setFilter]);

  /* ---- Zone 1 : les compteurs, calcules sur le jeu COMPLET ---- */

  const menuItems = useMemo(() => {
    const now = new Date();
    const live = allBookings.filter((booking) => booking.archivedAt == null);
    return SECTION_DEFS.map((def) => ({
      id: def.id,
      label: def.label,
      count: live.filter((booking) => matchSection(booking, def.id, now)).length,
    }));
  }, [allBookings]);

  /* ---- Zone 3 : toutes les stats du catalogue, depuis les donnees reelles ---- */

  const statValues = useMemo<Record<ReservationStatId, string>>(() => {
    const now = new Date();
    const live = allBookings.filter((booking) => booking.archivedAt == null);
    const upcoming = live.filter((booking) => {
      const status = statusOf(booking);
      if (status !== 'option' && status !== 'confirme') return false;
      return booking.startAt != null && booking.startAt.getTime() > now.getTime();
    }).length;
    // "En cours" et "En retard" ne se recouvrent PAS : une location sortie dont
    // le retour est depasse est comptee une seule fois, dans le retard. Sinon
    // les deux chiffres de l'en-tete parleraient des memes lignes sans le dire,
    // et le comptoir lirait deux fois le meme materiel dehors.
    const late = live.filter((booking) => isOverdue(booking, now)).length;
    const out = live.filter(
      (booking) => statusOf(booking) === 'sorti' && !isOverdue(booking, now),
    ).length;
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const revenue = live
      .filter((booking) => {
        if (statusOf(booking) === 'annule') return false;
        const start = booking.startAt;
        return (
          start != null &&
          start.getTime() >= monthStart.getTime() &&
          start.getTime() < nextMonth.getTime()
        );
      })
      .reduce((sum, booking) => sum + totalAmountOf(booking), 0);
    return {
      total: String(live.length),
      'a-venir': String(upcoming),
      'en-cours': String(out),
      'en-retard': String(late),
      'ca-mois': formatEuros(revenue),
    };
  }, [allBookings]);

  /*
   * L'ETAT VIDE DIT LA VERITE DE LA SECTION. Le chargeur filtre par section :
   * une section sans ligne rend donc `status.empty` vrai, et le texte "Aucune
   * location pour l'instant, cree la premiere" serait un MENSONGE quand la base
   * en contient quinze et qu'il n'y a simplement aucun retard. Zero retard est
   * une bonne nouvelle, pas un ecran vide a remplir.
   */
  const emptyState = useMemo(() => {
    if (allBookings.length === 0) {
      return {
        title: "Aucune location pour l'instant",
        hint: 'Clique sur "Ajouter", ou importe le jeu de demonstration depuis les reglages de l\'app.',
      };
    }
    switch (section) {
      case 'retard':
        return {
          title: 'Aucun retard',
          hint: 'Tout le materiel sorti doit encore rentrer dans les temps.',
        };
      case 'aujourdhui':
        return {
          title: "Rien aujourd'hui",
          hint: "Aucun depart ni retour prevu aujourd'hui. Le planning complet est dans la section Planning.",
        };
      default:
        return {
          title: 'Aucune location au planning',
          hint: 'Les locations annulees ne sont pas listees ici. Clique sur "Ajouter" pour en creer une.',
        };
    }
  }, [allBookings.length, section]);

  // Stats affichees : la selection de la pref, dans l'ordre du catalogue,
  // plafonnee a STATS_MAX (garde-fou si la pref stockee depasse le contrat).
  const headerStats = useMemo(
    () =>
      STAT_DEFS.filter((def) => statIds.includes(def.id))
        .slice(0, STATS_MAX)
        .map((def) => ({ value: statValues[def.id], label: def.label })),
    [statIds, statValues],
  );

  /* ------------------------------------------------------------------ */
  /* Le formulaire (Modal) : creation et modification                    */
  /* ------------------------------------------------------------------ */

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [clientId, setClientId] = useState('');
  const [newClientFirst, setNewClientFirst] = useState('');
  const [newClientLast, setNewClientLast] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [projectName, setProjectName] = useState('');
  const [crewContact, setCrewContact] = useState('');
  const [startDay, setStartDay] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDay, setEndDay] = useState('');
  const [endTime, setEndTime] = useState('18:00');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [depositText, setDepositText] = useState('');
  const [amountText, setAmountText] = useState('');
  const [amountTouched, setAmountTouched] = useState(false);
  const [statusValue, setStatusValue] = useState<BookingStatus>('option');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /*
   * La location que "Modifier" ouvrirait : celle de la fiche courante. On la
   * cherche dans le jeu COMPLET (elle peut avoir quitte la section affichee) et,
   * a defaut, dans le jeu de l'ecran. Introuvable, le bouton se DESACTIVE au
   * lieu de rester cliquable sans effet.
   */
  const openBooking = useMemo(() => {
    if (openId == null) return null;
    return (
      allBookings.find((candidate) => candidate.id === openId) ??
      screen.records.find((candidate) => candidate.id === openId) ??
      null
    );
  }, [openId, allBookings, screen.records]);

  const parcById = useMemo(() => new Map(parc.map((asset) => [asset.id, asset])), [parc]);

  const selectedAssets = useMemo(
    () =>
      selectedAssetIds
        .map((id) => parcById.get(id))
        .filter((asset): asset is Asset => asset != null),
    [selectedAssetIds, parcById],
  );

  // Materiel proposable : le disponible, plus celui deja engage par la location
  // qu'on modifie (il est `loue`, il doit rester cochable). Groupe par categorie.
  const materielGroups = useMemo(() => {
    const chosen = new Set(selectedAssetIds);
    const usable = parc.filter(
      (asset) =>
        asset.archivedAt == null && (asset.status === MATERIEL_AVAILABLE || chosen.has(asset.id)),
    );
    return CATEGORY_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
      assets: usable
        .filter((asset) => categoryOf(asset) === option.value)
        .sort((a, b) => a.name.localeCompare(b.name)),
    })).filter((group) => group.assets.length > 0);
  }, [parc, selectedAssetIds]);

  // Le montant PROPOSE : somme des tarifs journaliers x jours de location.
  // Il reste modifiable : une remise se negocie, elle ne se calcule pas.
  const rentalDayCount = useMemo(
    () =>
      rentalDays(
        fromDayAndTime(startDay, startTime) ?? undefined,
        fromDayAndTime(endDay, endTime) ?? undefined,
      ),
    [startDay, startTime, endDay, endTime],
  );
  const suggestedAmount = useMemo(
    () => Math.round(dailyTotalOf(selectedAssets) * rentalDayCount),
    [selectedAssets, rentalDayCount],
  );

  useEffect(() => {
    if (formMode == null || amountTouched) return;
    setAmountText(suggestedAmount > 0 ? String(suggestedAmount) : '');
  }, [formMode, amountTouched, suggestedAmount]);

  const closeForm = useCallback(() => {
    setFormMode(null);
    setEditingId(null);
    setClientId('');
    setNewClientFirst('');
    setNewClientLast('');
    setNewClientEmail('');
    setNewClientPhone('');
    setProjectName('');
    setCrewContact('');
    setStartDay('');
    setStartTime('09:00');
    setEndDay('');
    setEndTime('18:00');
    setSelectedAssetIds([]);
    setDepositText('');
    setAmountText('');
    setAmountTouched(false);
    setStatusValue('option');
    setFormError(null);
  }, []);

  const openCreate = useCallback(() => {
    const today = new Date();
    setFormMode('create');
    setEditingId(null);
    setClientId('');
    setNewClientFirst('');
    setNewClientLast('');
    setNewClientEmail('');
    setNewClientPhone('');
    setProjectName('');
    setCrewContact('');
    setStartDay(toDayInput(today));
    setStartTime('09:00');
    // La duree par defaut des reglages pre-remplit le retour : jours de
    // location BORNES COMPRISES, donc 3 jours = aujourd'hui plus deux.
    setEndDay(toDayInput(addDays(today, defaultDays - 1)));
    setEndTime('18:00');
    setSelectedAssetIds([]);
    setDepositText('');
    setAmountText('');
    setAmountTouched(false);
    setStatusValue('option');
    setFormError(null);
  }, [defaultDays]);

  const openEdit = useCallback(() => {
    const booking = openBooking;
    if (booking == null) return;
    setFormMode('edit');
    setEditingId(booking.id);
    setClientId(booking.contactId ?? '');
    setNewClientFirst('');
    setNewClientLast('');
    setNewClientEmail('');
    setNewClientPhone('');
    setProjectName(projectNameOf(booking));
    setCrewContact(crewContactOf(booking));
    setStartDay(toDayInput(booking.startAt));
    setStartTime(booking.startAt == null ? '09:00' : toTimeInput(booking.startAt));
    setEndDay(toDayInput(booking.endAt));
    setEndTime(booking.endAt == null ? '18:00' : toTimeInput(booking.endAt));
    setSelectedAssetIds(assetIdsOf(booking));
    setDepositText(String(depositOf(booking)));
    setAmountText(String(totalAmountOf(booking)));
    // Un montant deja negocie ne se fait pas ecraser par la proposition.
    setAmountTouched(true);
    setStatusValue(statusOf(booking));
    setFormError(null);
  }, [openBooking]);

  // Shortcut du dock (action `new` de la fiche taki.app.json) : on ouvre le
  // MEME Modal que le bouton de la zone 6, puis on marque l'action traitee.
  const { actionId: pendingActionId, consume: consumePendingAction } =
    usePendingAppAction(RESERVATIONS_APP_ID);
  useEffect(() => {
    if (pendingActionId == null) return;
    if (pendingActionId === 'new') openCreate();
    consumePendingAction();
  }, [pendingActionId, consumePendingAction, openCreate]);

  const toggleAsset = useCallback((assetId: string) => {
    setSelectedAssetIds((current) =>
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId],
    );
  }, []);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setFormError(null);

      const startAt = fromDayAndTime(startDay, startTime);
      const endAt = fromDayAndTime(endDay, endTime);
      if (startAt == null || endAt == null) {
        setFormError('Renseigne la date de depart et la date de retour.');
        return;
      }
      if (endAt.getTime() < startAt.getTime()) {
        setFormError('La date de retour doit suivre la date de depart.');
        return;
      }
      if (projectName.trim() === '') {
        setFormError('Le nom du projet est obligatoire.');
        return;
      }
      if (
        clientId === NEW_CLIENT &&
        (newClientFirst.trim() === '' || newClientLast.trim() === '')
      ) {
        setFormError('Renseigne le nom du nouveau client.');
        return;
      }

      setSaving(true);
      try {
        // Client inexistant : on le cree dans la famille `contacts`, puis on
        // rattache la location a son id reel.
        let contactId = clientId;
        if (clientId === NEW_CLIENT) {
          const created = await sdk.contacts.create({
            type: 'person',
            firstName: newClientFirst.trim(),
            lastName: newClientLast.trim(),
            ...(newClientEmail.trim() !== '' ? { email: newClientEmail.trim() } : {}),
            ...(newClientPhone.trim() !== '' ? { phone: newClientPhone.trim() } : {}),
            orgId: config.orgId,
          });
          contactId = created.id;
        }
        const linked = contactId !== '' && contactId !== NEW_CLIENT;

        const customFields = {
          projectName: projectName.trim(),
          crewContact: crewContact.trim(),
          assetIds: selectedAssetIds,
          deposit: toAmount(depositText),
          totalAmount: toAmount(amountText),
        };

        /*
         * Le parc suit ICI AUSSI, par la MEME fonction que le kanban : le
         * formulaire est l'autre endroit ou l'app ecrit un statut de location.
         * A la creation la regle ne bouge rien (une location nait en option ou
         * confirmee) : on l'appelle quand meme, pour qu'il n'existe qu'un seul
         * chemin, valable si le catalogue des statuts initiaux evolue.
         */
        let parcProblem: string | null = null;

        if (formMode === 'edit' && editingId != null) {
          // `type` et `orgId` sont immuables (Security Rules) : le patch ne les
          // porte pas. Le SDK fusionne `customFields` cle par cle.
          await sdk.bookings.update(editingId, {
            ...(linked ? { contactId } : {}),
            startAt,
            endAt,
            status: statusValue,
            customFields,
          });
          parcProblem = await syncParc(statusValue, selectedAssetIds);
          reload();
        } else {
          const created = await sdk.bookings.create({
            orgId: config.orgId,
            type: BOOKING_TYPE,
            ...(linked ? { contactId } : {}),
            startAt,
            endAt,
            status: statusValue,
            source: 'comptoir',
            customFields,
          });
          parcProblem = await syncParc(statusValue, selectedAssetIds);
          // Modele de la boite mail : un message qu'on a envoye soi-meme ne
          // s'affiche pas comme non lu. Marquee AVANT le rechargement (ou la
          // bascule de section, qui recharge aussi), sinon la location qu'on
          // vient de saisir revient du serveur avec son lisere et sa pastille.
          markRead(created.id);
          /*
           * Une location creee depuis une section qui ne la montre pas (En
           * retard, Aujourd'hui) semblerait ne rien faire : on bascule alors sur
           * le Planning, ou une location neuve est TOUJOURS visible. Sinon on
           * reste sur place et on recharge. Jamais les deux : changer de section
           * recharge deja tout seul (la section est dans la cle de chargement du
           * core), et `setSection` ne fait RIEN quand on lui redonne la section
           * courante, d'ou le `reload()` explicite dans ce cas.
           */
          const nextSection: SectionId = matchSection(created, section, new Date())
            ? section
            : DEFAULT_SECTION;
          if (nextSection === section) reload();
          else setSection(nextSection);
        }
        closeForm();
        // La location est ecrite : on ferme, PUIS on dit ce que le parc n'a pas
        // pu suivre. Garder le formulaire ouvert laisserait croire que rien
        // n'a ete enregistre.
        if (parcProblem != null) setParcError(parcProblem);
      } catch (cause) {
        setFormError(
          cause instanceof Error ? cause.message : "Echec de l'enregistrement de la location.",
        );
      } finally {
        setSaving(false);
      }
    },
    [
      sdk,
      config.orgId,
      formMode,
      editingId,
      clientId,
      newClientFirst,
      newClientLast,
      newClientEmail,
      newClientPhone,
      projectName,
      crewContact,
      startDay,
      startTime,
      endDay,
      endTime,
      selectedAssetIds,
      depositText,
      amountText,
      statusValue,
      section,
      setSection,
      markRead,
      reload,
      closeForm,
      syncParc,
    ],
  );

  const busy = screen.status.loading || screen.status.error != null;
  // En modification, tout le catalogue est ouvert (on annule, on rend, on sort) ;
  // a la creation, une location nait en option ou confirmee, jamais "retournee".
  const statusChoices =
    formMode === 'edit'
      ? BOOKING_STATUS_OPTIONS
      : BOOKING_STATUS_OPTIONS.filter((option) =>
          INITIAL_STATUS_VALUES.includes(option.value as BookingStatus),
        );

  return (
    <>
      <ScreenLayout
        /* Zone 1 : identite de l'app (puce + nom) et menu de sections. Les trois
           entrees decoupent la MEME collection ; leurs compteurs sont calcules
           sur le jeu complet. La section active vient du socle et y retourne :
           plus de transtypage au clic, `setSection` prend un id de menu tel
           quel, et c'est le core qui referme la fiche et recharge. */
        appName="Reservations"
        appIcon={<IconCalendarMark size={18} />}
        menuItems={menuItems}
        activeMenuId={section}
        onMenuSelect={setSection}
        /* Zone 2 : le h1 affiche le label de la section active ; `title` est le
           repli. */
        title="Locations"
        /* Zone 3 : stats selectionnees dans les reglages, valeurs toujours
           calculees des donnees reelles. */
        headerStats={headerStats}
        /* Zone 4, groupe defilant : recherche + pastilles visibles. */
        onSearchChange={screen.setSearch}
        searchValue={screen.query.search}
        searchPlaceholder="Rechercher un projet, un client"
        filters={screen.filters}
        activeFilterId={screen.activeFilterId}
        onFilterSelect={screen.setFilter}
        /* Zone 4, groupe EPINGLE : le selecteur de vues. Les cinq sont nourries. */
        views={screen.views}
        activeViewId={screen.viewId}
        onViewSelect={screen.setView}
        /* Zone 4, groupe de GAUCHE au niveau fiche : la puce de retour (qui
           porte aussi `Echap`) puis l'indicateur d'enregistrement. Passees SANS
           condition : le gabarit ne les rend qu'au niveau `record`, et une fiche
           sans elles serait une nasse (la recherche s'efface, aucune sortie
           n'apparait). */
        onRecordExit={() => screen.openRecord(null)}
        recordExitLabel={screen.views.find((view) => view.id === screen.lastCollectionViewId)?.label}
        saveState={screen.saveState}
        onRetrySave={screen.retrySave}
        /* Groupe EPINGLE au niveau fiche : le curseur prend la place du
           selecteur de vues, meme raison, meme absence de condition. */
        recordCursor={screen.cursor}
        onRecordNavigate={screen.openRecord}
        /* La zone 5 est PRETEE au ViewHost et aux vues. */
        scrollRef={screen.scrollRef}
        /* Actions de la section active. Contrat v2 : l'action d'ajout se
           declare `kind: 'add'`, le gabarit impose le libelle universel
           "Ajouter", IconPlus et le lime (regle G5). */
        quickActions={[
          {
            id: 'new',
            label: 'Ajouter',
            kind: 'add',
            disabled: busy,
            onClick: openCreate,
          },
          {
            id: 'edit',
            label: 'Modifier',
            disabled: busy || openBooking == null,
            onClick: openEdit,
          },
        ]}
        viewActions={screen.viewActions}
      >
        {/* Zone 5 pilotee par une VUE : le ViewHost tient les etats standard
            (erreur > chargement > fiche introuvable > vide > contenu), la
            memoire de defilement par vue et le filet anti-plantage. L'ecran ne
            passe donc NI loading, NI error, NI empty au gabarit. */}
        <ViewHost
          state={screen}
          errorTitle="Echec du chargement"
          retryLabel="Reessayer"
          emptyTitle={emptyState.title}
          emptyHint={emptyState.hint}
          notFoundTitle="Location introuvable"
          notFoundHint="Cette location a ete supprimee, ou tu n'y as pas acces."
        />
      </ScreenLayout>

      {/* Le formulaire vit dans un Modal, FRERE du ScreenLayout : il flotte
          au-dessus de tous les etats de la zone 5, etat vide compris (G6). */}
      <Modal
        open={formMode != null}
        onClose={closeForm}
        size="lg"
        title={formMode === 'edit' ? 'Modifier la reservation' : 'Nouvelle reservation'}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={saving}>
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="reservation-form"
              loading={saving}
            >
              {formMode === 'edit' ? 'Enregistrer' : 'Creer la reservation'}
            </Button>
          </>
        }
      >
        <form id="reservation-form" onSubmit={onSubmit} className="flex flex-col gap-5">
          {/* ---- Le client ---- */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="reservation-client" className="text-xs font-medium text-ink-500">
              Client
            </label>
            <select
              id="reservation-client"
              className={SELECT_CLASS}
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
            >
              <option value="">Choisir un client</option>
              {clients.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {`${contact.firstName} ${contact.lastName}`.trim()}
                </option>
              ))}
              <option value={NEW_CLIENT}>Nouveau client</option>
            </select>
          </div>

          {clientId === NEW_CLIENT && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  aria-label="Nom du nouveau client, premier mot"
                  placeholder="Societe, premier mot"
                  value={newClientFirst}
                  onChange={(event) => setNewClientFirst(event.target.value)}
                />
                <Input
                  aria-label="Nom du nouveau client, second mot"
                  placeholder="Societe, second mot"
                  value={newClientLast}
                  onChange={(event) => setNewClientLast(event.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  aria-label="Email du nouveau client"
                  type="email"
                  placeholder="Email (optionnel)"
                  value={newClientEmail}
                  onChange={(event) => setNewClientEmail(event.target.value)}
                />
                <Input
                  aria-label="Telephone du nouveau client"
                  placeholder="Telephone (optionnel)"
                  value={newClientPhone}
                  onChange={(event) => setNewClientPhone(event.target.value)}
                />
              </div>
            </div>
          )}

          {/* ---- Le tournage ---- */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-project" className="text-xs font-medium text-ink-500">
                Nom du projet
              </label>
              <Input
                id="reservation-project"
                placeholder="Court metrage Nordwand"
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-crew" className="text-xs font-medium text-ink-500">
                Contact tournage
              </label>
              <Input
                id="reservation-crew"
                placeholder="Marc Weber"
                value={crewContact}
                onChange={(event) => setCrewContact(event.target.value)}
              />
            </div>
          </div>

          {/* ---- Les dates ---- */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-start-day" className="text-xs font-medium text-ink-500">
                Depart
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  id="reservation-start-day"
                  type="date"
                  value={startDay}
                  onChange={(event) => setStartDay(event.target.value)}
                  required
                />
                <Input
                  aria-label="Heure de depart"
                  type="time"
                  value={startTime}
                  onChange={(event) => setStartTime(event.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-end-day" className="text-xs font-medium text-ink-500">
                Retour
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  id="reservation-end-day"
                  type="date"
                  value={endDay}
                  onChange={(event) => setEndDay(event.target.value)}
                  required
                />
                <Input
                  aria-label="Heure de retour"
                  type="time"
                  value={endTime}
                  onChange={(event) => setEndTime(event.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ---- Le materiel, groupe par categorie ---- */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-ink-500">
              {`Materiel loue (${selectedAssetIds.length} selectionne(s), ${daysLabel(rentalDayCount)})`}
            </p>
            {materielGroups.length === 0 ? (
              <p className="text-[0.8125rem] text-ink-400">
                Aucun materiel disponible dans le parc. Importe le jeu de demonstration de l'app
                Inventaire, ou libere du materiel.
              </p>
            ) : (
              materielGroups.map((group) => (
                <div key={group.value} className="flex flex-col gap-1">
                  <p className="text-[0.6875rem] uppercase tracking-wide text-ink-400">
                    {group.label}
                  </p>
                  <div className="flex flex-col gap-1">
                    {group.assets.map((asset) => (
                      <label
                        key={asset.id}
                        className="flex items-center gap-2.5 text-[0.8125rem] text-ink-900"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 shrink-0 cursor-pointer"
                          checked={selectedAssetIds.includes(asset.id)}
                          onChange={() => toggleAsset(asset.id)}
                        />
                        <span className="min-w-0 flex-1 truncate">{assetLabelOf(asset)}</span>
                        {/* "/ jour" n'est pas decoratif : sans lui, le chiffre se
                            lit comme le prix du materiel, pas comme son tarif. */}
                        <span className="shrink-0 text-xs text-ink-400">
                          {`${formatEuros(dailyRateOf(asset))} / jour`}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ---- L'argent et le statut ---- */}
          <div className="grid grid-cols-3 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-deposit" className="text-xs font-medium text-ink-500">
                Acompte (EUR)
              </label>
              <Input
                id="reservation-deposit"
                inputMode="numeric"
                placeholder="0"
                value={depositText}
                onChange={(event) => setDepositText(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-amount" className="text-xs font-medium text-ink-500">
                Montant total (EUR)
              </label>
              <Input
                id="reservation-amount"
                inputMode="numeric"
                placeholder="0"
                value={amountText}
                onChange={(event) => {
                  setAmountTouched(true);
                  setAmountText(event.target.value);
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="reservation-status" className="text-xs font-medium text-ink-500">
                Statut
              </label>
              <select
                id="reservation-status"
                className={SELECT_CLASS}
                value={statusValue}
                onChange={(event) => setStatusValue(event.target.value as BookingStatus)}
              >
                {statusChoices.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="text-[0.8125rem] text-ink-400">
            {suggestedAmount > 0
              ? `Propose : ${formatEuros(suggestedAmount)} (tarifs journaliers du materiel selectionne x ${daysLabel(rentalDayCount)}). Modifiable.`
              : 'Selectionne du materiel pour obtenir une proposition de montant.'}
          </p>

          {formError != null && (
            <p className="text-sm text-danger" role="alert">
              {formError}
            </p>
          )}
        </form>
      </Modal>

      {/* Le parc n'a pas suivi. La location, elle, EST enregistree : on ne fait
          donc reculer ni la carte du kanban ni le formulaire, on dit ce qui
          n'a pas pu etre repercute. Meme mecanique d'erreur que le formulaire
          (un etat, un paragraphe role="alert" en ton danger), meme surface
          (un Modal, frere du ScreenLayout). */}
      <Modal
        open={parcError != null}
        onClose={() => setParcError(null)}
        size="sm"
        title="Le parc n'a pas suivi"
        footer={
          <Button variant="secondary" onClick={() => setParcError(null)}>
            Fermer
          </Button>
        }
      >
        <p className="text-sm text-danger" role="alert">
          {parcError}
        </p>
      </Modal>
    </>
  );
}

export default ReservationsScreen;
